import { useEffect, useState } from 'react';

export type BsTheme = 'light' | 'dark';

const STORAGE_KEY = 'bs-theme';

function preferredTheme(): BsTheme {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    /* private browsing */
  }
  const prefersDark =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
  return prefersDark ? 'dark' : 'light';
}

/**
 * Persisted light/dark toggle, shared by every page inside the console shell.
 *
 * It also stamps `data-theme` on `<html>`, which is not decoration: the vendored
 * map renderers (M5-D7) read their palette from
 * `getComputedStyle(document.documentElement)` and repaint on a `data-theme`
 * mutation of that element. A canvas cannot inherit a custom property from an
 * ancestor `<div>` the way the rest of the console does, so the theme has to be
 * a fact about the *document*. `theme/tokens.css` carries the matching selector.
 */
export function useBsTheme(): { theme: BsTheme; toggle: () => void } {
  const [theme, setTheme] = useState<BsTheme>(preferredTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggle = (): void => {
    setTheme((previous) => {
      const next: BsTheme = previous === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* private browsing; the choice simply will not survive a reload */
      }
      return next;
    });
  };

  return { theme, toggle };
}
